import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApi, useTenantContext } from '../lib/api';

type TenantConfig = Record<string, unknown> & {
  tenant_id: string;
  config_version?: string | null;
};

type TenantConfigDocument = Record<string, unknown> & {
  schema_version: 'v0' | 'v2';
  tenant_id: string;
  config_version?: string | null;
};

type ConfigVersionSummary = {
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  config_version: string | null;
  created_at?: string | null;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  published_at?: string | null;
  published_by?: string | null;
  archived_at?: string | null;
  archived_by?: string | null;
  rollback_from_version?: string | null;
};

type ConfigDiffEntry = { path: string; before: unknown; after: unknown };

type TenantConfigResponse = {
  config: TenantConfig | null;
  draft_config?: TenantConfig | null;
  published_document?: TenantConfigDocument | null;
  draft_document?: TenantConfigDocument | null;
  versions?: ConfigVersionSummary[];
};

type MarketplaceLibrary = {
  vertical: TenantVertical;
  pack_id: string;
  pack_version: string;
  templates: Array<{ item_id: string; key: string; version: string; description: string }>;
  flows: Array<{ item_id: string; name: string; version: string; use_case: string }>;
};

type OperationalAuditEvent = {
  audit_id: string;
  action: string;
  actor_id: string | null;
  previous_state: Record<string, unknown> | null;
  new_state: Record<string, unknown> | null;
  reason: string | null;
  created_at: string;
};

type TenantVertical =
  | 'pet'
  | 'estetica'
  | 'academia'
  | 'auto'
  | 'clinica'
  | 'fitness'
  | 'servicos'
  | 'custom';
type OfferingType = 'SERVICE' | 'PRODUCT' | 'MEMBERSHIP' | 'OTHER';
type Priority = 'low' | 'medium' | 'high' | 'urgent';
type PriceStyle = 'range' | 'from' | 'staff_only';
type Intent = 'BOOKING' | 'QUOTE' | 'QUESTION' | 'CANCEL' | 'RESCHEDULE' | 'OTHER';
type TemplateKey =
  | 'received'
  | 'ask_missing_info'
  | 'routed_to_staff'
  | 'fallback_error'
  | 'out_of_scope'
  | 'emergency_notice';
type Weekday = 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY';

type HoursRow = { closed: boolean; start: string; end: string };
type StudioTemplates = Record<TemplateKey, string>;
type RequiredTextByIntent = Record<Intent, string>;
type HoursByDay = Record<Weekday, HoursRow>;

export type StudioForm = {
  tenant_id: string;
  config_version: string;
  business_name: string;
  timezone: string;
  vertical: TenantVertical;
  business_summary: string;
  contact_phone_display: string;
  whatsapp_phone_number_id: string;
  units_text: string;
  hours: HoursByDay;
  offerings_text: string;
  templates: StudioTemplates;
  policies: { can_share_prices: boolean; price_style: PriceStyle; deposit_enabled: boolean };
  required_fields_text: RequiredTextByIntent;
  anti_loop: {
    waiting_info_timeout_hours: number;
    max_auto_questions_per_case: number;
    max_waiting_turns: number;
    max_non_text_prompts: number;
  };
  urgency_keywords_text: string;
  spam_keywords_text: string;
  ai: {
    auto_ask_missing_info: boolean;
    route_on_low_confidence: boolean;
    confidence_threshold: number;
    free_reply_enabled: boolean;
    human_reply_suggestions_enabled: boolean;
    case_summary_enabled: boolean;
  };
  whatsapp_flow_enabled: boolean;
};

const WEEKDAYS: readonly { value: Weekday; label: string }[] = [
  { value: 'MONDAY', label: 'Segunda' },
  { value: 'TUESDAY', label: 'Terca' },
  { value: 'WEDNESDAY', label: 'Quarta' },
  { value: 'THURSDAY', label: 'Quinta' },
  { value: 'FRIDAY', label: 'Sexta' },
  { value: 'SATURDAY', label: 'Sabado' },
  { value: 'SUNDAY', label: 'Domingo' },
];
const INTENTS: readonly Intent[] = [
  'BOOKING',
  'QUOTE',
  'QUESTION',
  'CANCEL',
  'RESCHEDULE',
  'OTHER',
];
const RUNTIME_INTENTS: readonly Intent[] = ['BOOKING', 'QUOTE', 'QUESTION', 'OTHER'];
const TEMPLATE_KEYS: readonly TemplateKey[] = [
  'received',
  'ask_missing_info',
  'routed_to_staff',
  'fallback_error',
  'out_of_scope',
  'emergency_notice',
];
const SECTION_DEFS = [
  { id: 'business', label: 'Negocio' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'units', label: 'Unidades' },
  { id: 'hours', label: 'Horarios' },
  { id: 'offerings', label: 'Ofertas' },
  { id: 'templates', label: 'Templates' },
  { id: 'marketplace', label: 'Marketplace' },
  { id: 'policies', label: 'Politicas' },
  { id: 'required', label: 'Campos' },
  { id: 'limits', label: 'Limites' },
  { id: 'urgency', label: 'Urgencia' },
  { id: 'review', label: 'Revisao' },
] as const;
type StudioSection = (typeof SECTION_DEFS)[number]['id'];

const DEFAULT_TEMPLATES: StudioTemplates = {
  received: 'Recebemos sua mensagem. Ja vamos te ajudar.',
  ask_missing_info: 'Preciso de mais uma informacao: {{question}}',
  routed_to_staff: 'Encaminhei para o nosso time.',
  fallback_error: 'Tive um problema por aqui. Nosso time vai te ajudar.',
  out_of_scope: 'Vou conectar voce ao nosso time para orientar melhor.',
  emergency_notice: 'Vou priorizar seu atendimento e acionar nosso time agora.',
};
function prettyJson(value: unknown) {
  return JSON.stringify(value, null, 2);
}

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString('pt-BR') : '-';
}

function auditVersionLabel(event: OperationalAuditEvent) {
  const previous = String(event.previous_state?.config_version ?? '-');
  const next = String(event.new_state?.config_version ?? '-');
  return previous + ' -> ' + next;
}

function diffValue(value: unknown) {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

function rec(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function str(value: unknown, fallback = '') {
  return typeof value === 'string' ? value : fallback;
}

function bool(value: unknown, fallback: boolean) {
  return typeof value === 'boolean' ? value : fallback;
}

function num(value: unknown, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function csvToList(value: string) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function listToCsv(value: unknown) {
  return arr(value)
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter(Boolean)
    .join(', ');
}

function slug(value: string, fallback: string) {
  const out = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return out || fallback;
}

function splitLines(text: string) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function splitPipe(line: string) {
  return line.split('|').map((part) => part.trim());
}

function defaultHours(): HoursByDay {
  return Object.fromEntries(
    WEEKDAYS.map((day) => [
      day.value,
      { closed: day.value === 'SATURDAY' || day.value === 'SUNDAY', start: '09:00', end: '18:00' },
    ]),
  ) as HoursByDay;
}

function defaultRequiredText(): RequiredTextByIntent {
  return {
    BOOKING: 'customer.name | Qual seu nome?',
    QUOTE: 'customer.name | Qual seu nome?',
    QUESTION: '',
    CANCEL: 'customer.name | Qual seu nome?',
    RESCHEDULE: 'customer.name | Qual seu nome?',
    OTHER: '',
  };
}

function defaultOfferings(vertical: TenantVertical) {
  return vertical === 'clinica'
    ? 'CONSULTA | Consulta | SERVICE | 50 | high\nRETORNO | Retorno | SERVICE | 30 | medium'
    : 'AULA_EXPERIMENTAL | Aula experimental | SERVICE | 60 | medium\nPLANO_MENSAL | Plano mensal | MEMBERSHIP | 0 | medium';
}

export function blankForm(tenantId: string): StudioForm {
  return {
    tenant_id: tenantId,
    config_version: 'draft-studio-v1',
    business_name: '',
    timezone: 'America/Sao_Paulo',
    vertical: 'fitness',
    business_summary: '',
    contact_phone_display: '',
    whatsapp_phone_number_id: '',
    units_text: 'default | Matriz | America/Sao_Paulo | true |',
    hours: defaultHours(),
    offerings_text: defaultOfferings('fitness'),
    templates: { ...DEFAULT_TEMPLATES },
    policies: { can_share_prices: false, price_style: 'staff_only', deposit_enabled: false },
    required_fields_text: defaultRequiredText(),
    anti_loop: {
      waiting_info_timeout_hours: 24,
      max_auto_questions_per_case: 3,
      max_waiting_turns: 5,
      max_non_text_prompts: 2,
    },
    urgency_keywords_text: 'urgente, emergencia, dor forte',
    spam_keywords_text: 'spam, teste, propaganda',
    ai: {
      auto_ask_missing_info: true,
      route_on_low_confidence: true,
      confidence_threshold: 0.6,
      free_reply_enabled: false,
      human_reply_suggestions_enabled: true,
      case_summary_enabled: true,
    },
    whatsapp_flow_enabled: true,
  };
}

function parseUnits(form: StudioForm) {
  const rows = splitLines(form.units_text).map((line, index) => {
    const parts = splitPipe(line);
    const unitId = slug(parts[0] ?? '', index === 0 ? 'default' : 'unit_' + String(index + 1));
    const name = parts[1] || (index === 0 ? form.business_name : '');
    const timezone = parts[2] || form.timezone;
    const isDefault = String(parts[3] ?? '').toLowerCase() === 'true' || index === 0;
    const city = parts[4] ?? '';
    return {
      unit_id: unitId,
      name,
      timezone,
      is_default: isDefault,
      ...(city ? { address: { city, country: 'BR' } } : {}),
    };
  });
  return rows.length
    ? rows
    : [{ unit_id: 'default', name: form.business_name, timezone: form.timezone, is_default: true }];
}

function parseOfferings(text: string) {
  return splitLines(text)
    .map((line) => {
      const parts = splitPipe(line);
      const duration = Number(parts[3] ?? 0);
      return {
        code: slug(parts[0] ?? '', 'oferta').toUpperCase(),
        label: parts[1] ?? '',
        type: (parts[2] || 'SERVICE') as OfferingType,
        ...(duration > 0 ? { default_duration_min: Math.round(duration) } : {}),
        default_priority: (parts[4] || 'medium') as Priority,
      };
    })
    .filter((item) => item.code && item.label);
}
function parseRequiredFields(text: string) {
  return splitLines(text)
    .map((line) => {
      const parts = splitPipe(line);
      return { path: parts[0] ?? '', question: parts[1] ?? '' };
    })
    .filter((field) => field.path && field.question);
}

function requiredFieldsByIntent(form: StudioForm) {
  return Object.fromEntries(
    INTENTS.map((intent) => [intent, parseRequiredFields(form.required_fields_text[intent])]),
  ) as Record<Intent, { path: string; question: string }[]>;
}

function runtimeRequiredFields(form: StudioForm) {
  const byIntent = requiredFieldsByIntent(form);
  return Object.fromEntries(RUNTIME_INTENTS.map((intent) => [intent, byIntent[intent]]));
}

function cleanTemplates(templates: StudioTemplates): StudioTemplates {
  return Object.fromEntries(
    TEMPLATE_KEYS.map((key) => [key, templates[key].trim() || DEFAULT_TEMPLATES[key]]),
  ) as StudioTemplates;
}

function templateVars(template: string) {
  const found = new Set<string>();
  for (const match of template.matchAll(/\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g)) {
    if (match[1]) found.add(match[1]);
  }
  return [...found];
}

function templateCatalog(
  idPrefix: string,
  templates: StudioTemplates,
  scope: 'VERTICAL' | 'TENANT',
) {
  const prefix = slug(idPrefix, scope.toLowerCase());
  const base = TEMPLATE_KEYS.map((key) => ({
    id: prefix + '.' + key + '.v1',
    key,
    version: 'v1',
    language: 'pt-BR',
    scope,
    channel: 'CORE_REPLY',
    body: templates[key],
    variables: templateVars(templates[key]),
  }));
  return [
    ...base,
    {
      id: prefix + '.whatsapp.received.v1',
      key: 'received',
      version: 'v1',
      language: 'pt-BR',
      scope: 'WHATSAPP_APPROVED',
      channel: 'WHATSAPP',
      body: templates.received,
      variables: templateVars(templates.received),
      whatsapp_template_name: prefix + '_received_v1',
      approved: false,
    },
    {
      id: prefix + '.quick_reply.handoff.v1',
      key: 'routed_to_staff',
      version: 'v1',
      language: 'pt-BR',
      scope: 'QUICK_REPLY',
      channel: 'INTERNAL',
      body: 'Encaminhar para humano',
      variables: [],
    },
  ];
}

function hoursConfig(form: StudioForm) {
  return {
    timezone: form.timezone,
    weekly: WEEKDAYS.map((day) => ({
      day: day.value,
      closed: form.hours[day.value].closed,
      windows: form.hours[day.value].closed
        ? []
        : [{ start: form.hours[day.value].start, end: form.hours[day.value].end }],
    })),
  };
}

function flowsConfig(enabled: boolean) {
  const definitions = enabled
    ? [
        {
          flow_id: 'studio-booking-flow',
          flow_version: 'v1',
          name: 'Agendamento',
          channel: 'whatsapp',
          intents: ['BOOKING', 'RESCHEDULE'],
          use_case: 'BOOKING',
          enabled: true,
          cta_label: 'Agendar',
          screen: 'BOOKING',
          response_fields: [
            { key: 'customer_name', path: 'customer.name', required: true },
            { key: 'period', path: 'schedule_preference.period', required: true },
            { key: 'specific_date', path: 'schedule_preference.specific_date' },
            { key: 'phone', path: 'customer.phone' },
          ],
        },
      ]
    : [];
  return {
    whatsapp: {
      enabled,
      suggest_on_intents: enabled ? ['BOOKING', 'RESCHEDULE'] : [],
      min_missing_fields_for_flow: 2,
    },
    definitions,
  };
}

function usersRoles() {
  return {
    default_role: 'AGENT',
    enabled_roles: ['OWNER', 'ADMIN', 'SUPERVISOR', 'AGENT', 'VIEWER'],
  };
}

function domainVocabulary(form: StudioForm, offerings: ReturnType<typeof parseOfferings>) {
  return {
    terms: offerings.map((offering) => ({
      canonical: offering.code,
      synonyms: [offering.label.toLowerCase()],
      maps_to_offering_code: offering.code,
    })),
    sensitive_terms:
      form.vertical === 'clinica'
        ? ['diagnostico', 'exame', 'laudo', 'receita', 'sintoma']
        : ['lesao', 'dor forte', 'saude'],
    out_of_scope_terms: ['bitcoin', 'cripto', 'politica', 'hack', 'fraude'],
  };
}

function riskRules(form: StudioForm) {
  const rules = [
    {
      rule_id: 'studio-spam-silence',
      description: 'Silenciar spam configurado pelo tenant.',
      severity: 'medium',
      action: 'SILENCE_SPAM',
      keywords: csvToList(form.spam_keywords_text),
    },
    {
      rule_id: 'studio-emergency-mark',
      description: 'Marcar emergencia por palavras configuradas.',
      severity: 'high',
      action: 'MARK_EMERGENCY',
      keywords: csvToList(form.urgency_keywords_text),
    },
  ];
  if (form.vertical !== 'clinica') return rules;
  return [
    ...rules,
    {
      rule_id: 'studio-clinic-ai-skip',
      description: 'Conteudo clinico sensivel bloqueia IA livre.',
      severity: 'high',
      action: 'SKIP_AI',
      sensitive_categories: ['health', 'document', 'phone', 'email'],
      suppress_ai_context_fields: ['business_profile.notes', 'triaged_payload.constraints'],
    },
  ];
}
export function buildTenantConfigDocument(form: StudioForm): TenantConfigDocument {
  const businessName = form.business_name.trim();
  const tenantId = form.tenant_id.trim();
  const templates = cleanTemplates(form.templates);
  const offerings = parseOfferings(form.offerings_text);
  const requiredByIntent = requiredFieldsByIntent(form);
  const requiredRuntime = runtimeRequiredFields(form);
  const antiLoop = {
    waiting_info_timeout_hours: Math.max(1, Math.round(form.anti_loop.waiting_info_timeout_hours)),
    max_auto_questions_per_case: Math.max(
      1,
      Math.round(form.anti_loop.max_auto_questions_per_case),
    ),
    max_waiting_turns: Math.max(1, Math.round(form.anti_loop.max_waiting_turns)),
    max_non_text_prompts: Math.max(1, Math.round(form.anti_loop.max_non_text_prompts)),
  };
  const urgencySpam = {
    urgent_keywords: csvToList(form.urgency_keywords_text),
    spam_keywords: csvToList(form.spam_keywords_text),
  };
  const ai = {
    auto_ask_missing_info: form.ai.auto_ask_missing_info,
    route_on_low_confidence: form.ai.route_on_low_confidence,
    confidence_threshold: Math.max(0, Math.min(1, form.ai.confidence_threshold)),
    free_reply_enabled: form.ai.free_reply_enabled,
    human_reply_suggestions_enabled: form.ai.human_reply_suggestions_enabled,
    case_summary_enabled: form.ai.case_summary_enabled,
  };
  const flows = flowsConfig(form.whatsapp_flow_enabled);
  const identity: Record<string, unknown> = {
    business_name: businessName,
    timezone: form.timezone,
    vertical: form.vertical,
  };
  if (form.business_summary.trim()) {
    identity.business_profile = {
      summary: form.business_summary.trim(),
      ...(form.contact_phone_display.trim()
        ? { contact_phone_display: form.contact_phone_display.trim() }
        : {}),
      accepted_benefits: [],
      plans: [],
      class_schedules: [],
      notes: [],
    };
  }
  const channels = {
    whatsapp: {
      enabled: Boolean(form.whatsapp_phone_number_id.trim()),
      business_phone_number_ids: form.whatsapp_phone_number_id.trim()
        ? [form.whatsapp_phone_number_id.trim()]
        : [],
    },
    console: { enabled: true },
  };
  const pack = {
    vertical: form.vertical,
    pack_id: form.vertical + '-studio-pack',
    pack_version: 'studio-v1',
    supported_intents: INTENTS,
    required_fields_by_intent: requiredByIntent,
    service_offerings: offerings,
    templates,
    template_catalog: templateCatalog(form.vertical + '.studio', templates, 'VERTICAL'),
    policies: form.policies,
    required_fields: requiredRuntime,
    anti_loop_limits: antiLoop,
    urgency_spam: urgencySpam,
    domain_vocabulary: domainVocabulary(form, offerings),
    risk_rules: riskRules(form),
    urgency_rules: urgencySpam.urgent_keywords.length
      ? [
          {
            rule_id: 'studio-urgent-keywords',
            keywords: urgencySpam.urgent_keywords,
            priority: 'urgent',
          },
        ]
      : [],
    ai,
    flows,
    recommended_flows: flows.definitions,
    conversation_examples: [
      {
        example_id: 'studio-booking-example',
        user_message: 'Quero agendar um atendimento',
        expected_intent: 'BOOKING',
      },
    ],
    golden_tests: [],
    metrics: [
      {
        metric_id: 'studio_handoff_rate',
        label: 'Taxa de handoff',
        aggregation: 'rate',
        dimensions: ['intent', 'priority'],
      },
    ],
    users_roles: usersRoles(),
    integrations: {},
  };
  return {
    schema_version: 'v2',
    tenant_id: tenantId,
    config_version: form.config_version.trim() || 'draft-studio-v1',
    core: {
      identity,
      channels,
      business_hours: hoursConfig(form),
      units: parseUnits(form),
      users_roles: usersRoles(),
      integrations: {},
    },
    vertical_pack: pack,
    tenant_customization: {
      identity,
      channels,
      business_hours: hoursConfig(form),
      units: parseUnits(form),
      supported_intents: INTENTS,
      required_fields_by_intent: requiredByIntent,
      service_offerings: offerings,
      templates,
      template_catalog: templateCatalog(tenantId, templates, 'TENANT'),
      policies: form.policies,
      required_fields: requiredRuntime,
      anti_loop_limits: antiLoop,
      urgency_spam: urgencySpam,
      domain_vocabulary: domainVocabulary(form, offerings),
      risk_rules: riskRules(form),
      urgency_rules: pack.urgency_rules,
      ai,
      flows,
      recommended_flows: flows.definitions,
      users_roles: usersRoles(),
      integrations: {},
    },
  };
}

export function validateStudioForm(form: StudioForm): string[] {
  const errors: string[] = [];
  if (!form.tenant_id.trim()) errors.push('Tenant ausente no contexto do usuario.');
  if (!form.business_name.trim()) errors.push('Informe o nome do negocio.');
  if (!form.timezone.trim()) errors.push('Informe o timezone.');
  if (!form.whatsapp_phone_number_id.trim()) errors.push('Informe o phone number id do WhatsApp.');
  if (!parseUnits(form).some((unit) => unit.name.trim()))
    errors.push('Cadastre pelo menos uma unidade.');
  if (!parseUnits(form).some((unit) => unit.is_default)) errors.push('Marque uma unidade padrao.');
  if (!parseOfferings(form.offerings_text).length)
    errors.push('Cadastre pelo menos um servico, produto ou plano.');
  if (!csvToList(form.urgency_keywords_text).length)
    errors.push('Informe pelo menos uma palavra de urgencia.');
  if (form.ai.confidence_threshold < 0 || form.ai.confidence_threshold > 1) {
    errors.push('Threshold de confianca deve ficar entre 0 e 1.');
  }
  if (form.anti_loop.waiting_info_timeout_hours <= 0) {
    errors.push('Timeout de espera deve ser maior que zero.');
  }
  if (form.anti_loop.max_auto_questions_per_case <= 0) {
    errors.push('Maximo de perguntas automaticas deve ser maior que zero.');
  }
  for (const day of WEEKDAYS) {
    const row = form.hours[day.value];
    if (!row.closed && row.start >= row.end) errors.push(day.label + ': horario invalido.');
  }
  for (const key of TEMPLATE_KEYS) {
    if (!form.templates[key].trim()) errors.push('Template ' + key + ' e obrigatorio.');
  }
  for (const intent of INTENTS) {
    splitLines(form.required_fields_text[intent]).forEach((line, index) => {
      const parts = splitPipe(line);
      if (!parts[0] || !parts[1])
        errors.push(intent + ' campo ' + String(index + 1) + ': use path | pergunta.');
    });
  }
  return errors;
}
function requiredTextFromObject(value: unknown): RequiredTextByIntent {
  const source = rec(value);
  return Object.fromEntries(
    INTENTS.map((intent) => [
      intent,
      arr(source[intent])
        .map((item) => {
          const field = rec(item);
          return str(field.path) && str(field.question)
            ? str(field.path) + ' | ' + str(field.question)
            : '';
        })
        .filter(Boolean)
        .join('\n'),
    ]),
  ) as RequiredTextByIntent;
}

function unitsTextFromObject(value: unknown, fallbackTimezone: string) {
  return arr(value)
    .map((item) => {
      const unit = rec(item);
      const address = rec(unit.address);
      return [
        str(unit.unit_id, 'default'),
        str(unit.name),
        str(unit.timezone, fallbackTimezone),
        String(bool(unit.is_default, false)),
        str(address.city),
      ].join(' | ');
    })
    .join('\n');
}

function offeringsTextFromObject(value: unknown) {
  return arr(value)
    .map((item) => {
      const offering = rec(item);
      return [
        str(offering.code),
        str(offering.label),
        str(offering.type, 'SERVICE'),
        String(num(offering.default_duration_min, 0)),
        str(offering.default_priority, 'medium'),
      ].join(' | ');
    })
    .join('\n');
}

function hoursFromObject(value: unknown) {
  const out = defaultHours();
  for (const item of arr(rec(value).weekly)) {
    const row = rec(item);
    const day = str(row.day) as Weekday;
    if (!out[day]) continue;
    const firstWindow = rec(arr(row.windows)[0]);
    out[day] = {
      closed: bool(row.closed, arr(row.windows).length === 0),
      start: str(firstWindow.start, out[day].start),
      end: str(firstWindow.end, out[day].end),
    };
  }
  return out;
}

function templatesFromObject(value: unknown): StudioTemplates {
  const source = rec(value);
  return Object.fromEntries(
    TEMPLATE_KEYS.map((key) => [key, str(source[key], DEFAULT_TEMPLATES[key])]),
  ) as StudioTemplates;
}

function formFromDocument(
  document: TenantConfigDocument | TenantConfig | null,
  fallbackTenantId: string,
): StudioForm {
  if (!document) return blankForm(fallbackTenantId);
  if (document.schema_version !== 'v2') {
    const base = blankForm(str(document.tenant_id, fallbackTenantId));
    const policies = rec(document.policies);
    const flags = rec(document.flags);
    const triage = rec(document.triage_rules);
    return {
      ...base,
      config_version: str(document.config_version, base.config_version),
      business_name: str(document.name, ''),
      timezone: str(document.timezone, base.timezone),
      vertical: str(document.vertical, base.vertical) as TenantVertical,
      offerings_text: offeringsTextFromObject(document.offerings) || base.offerings_text,
      templates: templatesFromObject(document.message_templates),
      policies: {
        can_share_prices: bool(policies.can_share_prices, false),
        price_style: str(policies.price_style, 'staff_only') as PriceStyle,
        deposit_enabled: bool(policies.deposit_enabled, false),
      },
      required_fields_text: requiredTextFromObject(document.required_fields),
      anti_loop: {
        waiting_info_timeout_hours: num(flags.waiting_info_timeout_hours, 24),
        max_auto_questions_per_case: num(flags.max_auto_questions_per_case, 3),
        max_waiting_turns: num(flags.max_waiting_turns, 5),
        max_non_text_prompts: num(flags.max_non_text_prompts, 2),
      },
      urgency_keywords_text: listToCsv(triage.urgent_keywords),
      spam_keywords_text: listToCsv(triage.spam_keywords),
      ai: {
        auto_ask_missing_info: bool(flags.auto_ask_missing_info, true),
        route_on_low_confidence: bool(flags.route_on_low_confidence, true),
        confidence_threshold: num(flags.confidence_threshold, 0.6),
        free_reply_enabled: false,
        human_reply_suggestions_enabled: bool(flags.human_reply_suggestions_enabled, true),
        case_summary_enabled: bool(flags.case_summary_enabled, true),
      },
    };
  }

  const core = rec(document.core);
  const pack = rec(document.vertical_pack);
  const custom = rec(document.tenant_customization);
  const coreIdentity = rec(core.identity);
  const customIdentity = rec(custom.identity);
  const identity = Object.keys(customIdentity).length ? customIdentity : coreIdentity;
  const vertical = str(identity.vertical, str(pack.vertical, 'fitness')) as TenantVertical;
  const timezone = str(identity.timezone, 'America/Sao_Paulo');
  const profile = rec(identity.business_profile);
  const customChannels = rec(custom.channels);
  const channels = Object.keys(customChannels).length ? customChannels : rec(core.channels);
  const whatsapp = rec(channels.whatsapp);
  const policies = rec(custom.policies ?? pack.policies);
  const antiLoop = rec(custom.anti_loop_limits ?? pack.anti_loop_limits);
  const urgency = rec(custom.urgency_spam ?? pack.urgency_spam);
  const ai = rec(custom.ai ?? pack.ai);
  const flows = rec(custom.flows ?? pack.flows);
  const flowWhatsapp = rec(flows.whatsapp);

  return {
    ...blankForm(str(document.tenant_id, fallbackTenantId)),
    config_version: str(document.config_version, 'draft-studio-v1'),
    business_name: str(identity.business_name),
    timezone,
    vertical,
    business_summary: str(profile.summary),
    contact_phone_display: str(profile.contact_phone_display),
    whatsapp_phone_number_id: str(arr(whatsapp.business_phone_number_ids)[0]),
    units_text: unitsTextFromObject(custom.units ?? core.units, timezone),
    hours: hoursFromObject(custom.business_hours ?? core.business_hours),
    offerings_text:
      offeringsTextFromObject(custom.service_offerings ?? pack.service_offerings) ||
      defaultOfferings(vertical),
    templates: templatesFromObject(custom.templates ?? pack.templates),
    policies: {
      can_share_prices: bool(policies.can_share_prices, false),
      price_style: str(policies.price_style, 'staff_only') as PriceStyle,
      deposit_enabled: bool(policies.deposit_enabled, false),
    },
    required_fields_text: requiredTextFromObject(custom.required_fields ?? pack.required_fields),
    anti_loop: {
      waiting_info_timeout_hours: num(antiLoop.waiting_info_timeout_hours, 24),
      max_auto_questions_per_case: num(antiLoop.max_auto_questions_per_case, 3),
      max_waiting_turns: num(antiLoop.max_waiting_turns, 5),
      max_non_text_prompts: num(antiLoop.max_non_text_prompts, 2),
    },
    urgency_keywords_text: listToCsv(urgency.urgent_keywords),
    spam_keywords_text: listToCsv(urgency.spam_keywords),
    ai: {
      auto_ask_missing_info: bool(ai.auto_ask_missing_info, true),
      route_on_low_confidence: bool(ai.route_on_low_confidence, true),
      confidence_threshold: num(ai.confidence_threshold, 0.6),
      free_reply_enabled: bool(ai.free_reply_enabled, false),
      human_reply_suggestions_enabled: bool(ai.human_reply_suggestions_enabled, true),
      case_summary_enabled: bool(ai.case_summary_enabled, true),
    },
    whatsapp_flow_enabled: bool(flowWhatsapp.enabled, false),
  };
}

function sameForm(left: StudioForm, right: StudioForm) {
  return JSON.stringify(left) === JSON.stringify(right);
}
export default function ConfigPage() {
  const { tenant_id, hasScope } = useTenantContext();
  const { request } = useApi();
  const [activeSection, setActiveSection] = useState<StudioSection>('business');
  const [form, setForm] = useState<StudioForm>(() => blankForm(tenant_id ?? ''));
  const [savedForm, setSavedForm] = useState<StudioForm>(() => blankForm(tenant_id ?? ''));
  const [publishedConfig, setPublishedConfig] = useState<TenantConfig | null>(null);
  const [draftConfig, setDraftConfig] = useState<TenantConfig | null>(null);
  const [draftDocument, setDraftDocument] = useState<TenantConfigDocument | null>(null);
  const [versions, setVersions] = useState<ConfigVersionSummary[]>([]);
  const [diffEntries, setDiffEntries] = useState<ConfigDiffEntry[]>([]);
  const [auditEvents, setAuditEvents] = useState<OperationalAuditEvent[]>([]);
  const [changeReason, setChangeReason] = useState('');
  const [validationReason, setValidationReason] = useState('');
  const [publishReason, setPublishReason] = useState('');
  const [rollbackReason, setRollbackReason] = useState('');
  const [rollbackVersion, setRollbackVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [marketplace, setMarketplace] = useState<MarketplaceLibrary | null>(null);
  const [marketplaceVertical, setMarketplaceVertical] = useState<TenantVertical>('fitness');

  const canChange = hasScope('CONFIG_CHANGE');
  const canPublish = hasScope('CONFIG_PUBLISH');
  const validationErrors = useMemo(() => validateStudioForm(form), [form]);
  const currentDocument = useMemo(() => buildTenantConfigDocument(form), [form]);
  const hasDraft = Boolean(draftConfig || draftDocument);
  const isDirty = !sameForm(form, savedForm);

  const refresh = useCallback(async () => {
    if (!tenant_id || !canChange) return;
    setLoading(true);
    setError(null);
    try {
      const [configOut, auditOut] = await Promise.all([
        request<TenantConfigResponse>('/tenants/' + encodeURIComponent(tenant_id) + '/config'),
        request<{ items: OperationalAuditEvent[] }>(
          '/tenants/' + encodeURIComponent(tenant_id) + '/config/audit',
        ).catch(() => ({ items: [] })),
      ]);
      const nextForm = formFromDocument(
        configOut.draft_document ??
          configOut.published_document ??
          configOut.draft_config ??
          configOut.config,
        tenant_id,
      );
      setPublishedConfig(configOut.config);
      setDraftConfig(configOut.draft_config ?? null);
      setDraftDocument(configOut.draft_document ?? null);
      setVersions(configOut.versions ?? []);
      setForm(nextForm);
      setSavedForm(nextForm);
      setAuditEvents(auditOut.items);
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setLoading(false);
    }
  }, [canChange, request, tenant_id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function patchForm(patch: Partial<StudioForm>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function patchTemplate(key: TemplateKey, value: string) {
    patchForm({ templates: { ...form.templates, [key]: value } });
  }

  function patchRequired(intent: Intent, value: string) {
    patchForm({ required_fields_text: { ...form.required_fields_text, [intent]: value } });
  }

  function patchHour(day: Weekday, patch: Partial<HoursRow>) {
    patchForm({ hours: { ...form.hours, [day]: { ...form.hours[day], ...patch } } });
  }

  async function saveConfig(event: FormEvent) {
    event.preventDefault();
    if (!tenant_id || !canChange) return;
    if (validationErrors.length) {
      setError('Corrija os erros antes de salvar o draft.');
      setActiveSection('review');
      return;
    }
    setError(null);
    setSuccess(null);
    setSaving('save');
    try {
      const document = buildTenantConfigDocument({ ...form, tenant_id });
      const out = await request<{
        config: TenantConfig;
        draft_config?: TenantConfig;
        draft_document?: TenantConfigDocument;
        audit: OperationalAuditEvent;
      }>('/tenants/' + encodeURIComponent(tenant_id) + '/config', {
        method: 'PUT',
        body: JSON.stringify({ config: document, reason: changeReason }),
      });
      const saved = formFromDocument(out.draft_document ?? document, tenant_id);
      setDraftConfig(out.draft_config ?? out.config);
      setDraftDocument(out.draft_document ?? document);
      setForm(saved);
      setSavedForm(saved);
      setAuditEvents((current) => [...current, out.audit]);
      setDiffEntries([]);
      setChangeReason('');
      setSuccess('Draft salvo com auditoria.');
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(null);
    }
  }

  async function validateDraft(event: FormEvent) {
    event.preventDefault();
    if (!tenant_id || !canChange) return;
    if (validationErrors.length || isDirty || !hasDraft) {
      setError('Salve um draft valido antes de validar.');
      setActiveSection('review');
      return;
    }
    setError(null);
    setSuccess(null);
    setSaving('validate');
    try {
      const out = await request<{
        config: TenantConfig;
        draft_config?: TenantConfig;
        audit: OperationalAuditEvent;
      }>('/tenants/' + encodeURIComponent(tenant_id) + '/config/validate', {
        method: 'POST',
        body: JSON.stringify({ reason: validationReason }),
      });
      setDraftConfig(out.draft_config ?? out.config);
      setAuditEvents((current) => [...current, out.audit]);
      setValidationReason('');
      setSuccess('Draft validado.');
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(null);
    }
  }
  async function loadDiff() {
    if (!tenant_id || !canChange) return;
    if (!hasDraft || isDirty) {
      setError('Salve o draft antes de comparar.');
      return;
    }
    setError(null);
    setSuccess(null);
    setSaving('diff');
    try {
      const out = await request<{ diff: ConfigDiffEntry[] }>(
        '/tenants/' + encodeURIComponent(tenant_id) + '/config/diff',
      );
      setDiffEntries(out.diff);
      setSuccess('Diff carregado.');
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(null);
    }
  }

  async function publishConfig(event: FormEvent) {
    event.preventDefault();
    if (!tenant_id || !canPublish) return;
    if (validationErrors.length || isDirty || !hasDraft) {
      setError('Publicacao bloqueada: salve e valide um draft sem erros.');
      setActiveSection('review');
      return;
    }
    setError(null);
    setSuccess(null);
    setSaving('publish');
    try {
      const out = await request<{ config: TenantConfig; audit: OperationalAuditEvent }>(
        '/tenants/' + encodeURIComponent(tenant_id) + '/config/publish',
        { method: 'POST', body: JSON.stringify({ reason: publishReason }) },
      );
      setPublishedConfig(out.config);
      setDraftConfig(null);
      setDraftDocument(null);
      setSavedForm(form);
      setAuditEvents((current) => [...current, out.audit]);
      setDiffEntries([]);
      setPublishReason('');
      setSuccess('Configuracao publicada.');
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(null);
    }
  }

  async function rollbackConfig(event: FormEvent) {
    event.preventDefault();
    if (!tenant_id || !canPublish) return;
    setError(null);
    setSuccess(null);
    setSaving('rollback');
    try {
      const out = await request<{ config: TenantConfig; audit: OperationalAuditEvent }>(
        '/tenants/' + encodeURIComponent(tenant_id) + '/config/rollback',
        {
          method: 'POST',
          body: JSON.stringify({
            reason: rollbackReason,
            target_config_version: rollbackVersion.trim() || null,
          }),
        },
      );
      setPublishedConfig(out.config);
      setDraftConfig(null);
      setDraftDocument(null);
      setAuditEvents((current) => [...current, out.audit]);
      setDiffEntries([]);
      setRollbackReason('');
      setRollbackVersion('');
      setSuccess('Rollback publicado.');
      await refresh();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(null);
    }
  }

  async function loadMarketplace() {
    if (!tenant_id || !canChange) return;
    setError(null);
    setSuccess(null);
    setSaving('marketplace-load');
    try {
      const out = await request<{ library: MarketplaceLibrary }>(
        `/tenants/${encodeURIComponent(tenant_id)}/marketplace?vertical=${marketplaceVertical}`,
      );
      setMarketplace(out.library);
      setSuccess('Biblioteca carregada.');
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(null);
    }
  }

  async function installMarketplace(itemType: 'template' | 'flow', itemId: string) {
    if (!tenant_id || !canChange) return;
    setError(null);
    setSuccess(null);
    setSaving(`marketplace-${itemId}`);
    try {
      await request(`/tenants/${encodeURIComponent(tenant_id)}/marketplace/install`, {
        method: 'POST',
        body: JSON.stringify({
          vertical: marketplaceVertical,
          ...(itemType === 'template' ? { template_ids: [itemId] } : { flow_ids: [itemId] }),
          reason: `instalar ${itemType} ${itemId} pelo marketplace`,
        }),
      });
      setSuccess('Item instalado como draft.');
      await refresh();
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(null);
    }
  }

  if (!tenant_id) return <main className="page">Usuario sem tenant configurado.</main>;
  if (!canChange) return <main className="page">Sem permissao para configuracao.</main>;

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Admin Studio</h1>
          <p>
            Tenant {tenant_id} - {form.config_version || 'sem versao'}
          </p>
        </div>
        <div className="header-actions">
          <Link className="nav-link" to="/simulation">
            Simulacao
          </Link>
          {hasScope('USERS_ADMIN') ? (
            <Link className="nav-link" to="/users">
              Usuarios
            </Link>
          ) : null}
          <Link className="nav-link" to="/">
            Inbox
          </Link>
        </div>
      </header>

      {loading ? <p className="hint">Carregando configuracao...</p> : null}
      {error ? <p className="error">{error}</p> : null}
      {success ? <p className="success">{success}</p> : null}

      <section className="studio-shell">
        <nav className="studio-nav" aria-label="Secoes de configuracao">
          {SECTION_DEFS.map((section) => (
            <button
              key={section.id}
              className={activeSection === section.id ? 'active' : ''}
              type="button"
              onClick={() => setActiveSection(section.id)}
            >
              {section.label}
            </button>
          ))}
        </nav>

        <form className="panel studio-form" onSubmit={saveConfig}>
          {' '}
          {activeSection === 'business' ? (
            <section className="studio-section">
              <h2>Dados do negocio</h2>
              <div className="studio-grid">
                <label>
                  Nome do negocio
                  <input
                    value={form.business_name}
                    onChange={(event) => patchForm({ business_name: event.target.value })}
                  />
                </label>
                <label>
                  Vertical
                  <select
                    value={form.vertical}
                    onChange={(event) => {
                      const vertical = event.target.value as TenantVertical;
                      patchForm({ vertical, offerings_text: defaultOfferings(vertical) });
                    }}
                  >
                    <option value="fitness">Fitness</option>
                    <option value="clinica">Clinica</option>
                    <option value="academia">Academia</option>
                    <option value="estetica">Estetica</option>
                    <option value="servicos">Servicos</option>
                    <option value="pet">Pet</option>
                    <option value="auto">Auto</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>
                <label>
                  Timezone
                  <input
                    value={form.timezone}
                    onChange={(event) => patchForm({ timezone: event.target.value })}
                  />
                </label>
                <label>
                  Versao do draft
                  <input
                    value={form.config_version}
                    onChange={(event) => patchForm({ config_version: event.target.value })}
                  />
                </label>
              </div>
              <label>
                Resumo operacional
                <textarea
                  rows={5}
                  value={form.business_summary}
                  onChange={(event) => patchForm({ business_summary: event.target.value })}
                />
              </label>
              <label>
                Telefone visivel
                <input
                  value={form.contact_phone_display}
                  onChange={(event) => patchForm({ contact_phone_display: event.target.value })}
                />
              </label>
            </section>
          ) : null}
          {activeSection === 'whatsapp' ? (
            <section className="studio-section">
              <h2>WhatsApp</h2>
              <div className="studio-grid">
                <label>
                  Phone number id
                  <input
                    value={form.whatsapp_phone_number_id}
                    onChange={(event) =>
                      patchForm({ whatsapp_phone_number_id: event.target.value })
                    }
                  />
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={form.whatsapp_flow_enabled}
                    onChange={(event) => patchForm({ whatsapp_flow_enabled: event.target.checked })}
                  />
                  Sugerir WhatsApp Flow para agendamento
                </label>
              </div>
            </section>
          ) : null}
          {activeSection === 'units' ? (
            <section className="studio-section">
              <h2>Unidades</h2>
              <label>
                Uma por linha: codigo | nome | timezone | padrao true/false | cidade | fila |
                servicos
                <textarea
                  rows={8}
                  value={form.units_text}
                  onChange={(event) => patchForm({ units_text: event.target.value })}
                />
              </label>
            </section>
          ) : null}
          {activeSection === 'hours' ? (
            <section className="studio-section">
              <h2>Horarios</h2>
              <div className="studio-repeat">
                {WEEKDAYS.map((day) => (
                  <div className="studio-hours-row" key={day.value}>
                    <strong>{day.label}</strong>
                    <label className="check-row">
                      <input
                        type="checkbox"
                        checked={form.hours[day.value].closed}
                        onChange={(event) => patchHour(day.value, { closed: event.target.checked })}
                      />
                      Fechado
                    </label>
                    <label>
                      Abre
                      <input
                        type="time"
                        disabled={form.hours[day.value].closed}
                        value={form.hours[day.value].start}
                        onChange={(event) => patchHour(day.value, { start: event.target.value })}
                      />
                    </label>
                    <label>
                      Fecha
                      <input
                        type="time"
                        disabled={form.hours[day.value].closed}
                        value={form.hours[day.value].end}
                        onChange={(event) => patchHour(day.value, { end: event.target.value })}
                      />
                    </label>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
          {activeSection === 'offerings' ? (
            <section className="studio-section">
              <h2>Servicos e ofertas</h2>
              <label>
                Uma por linha: codigo | nome | tipo | duracao_min | prioridade
                <textarea
                  rows={9}
                  value={form.offerings_text}
                  onChange={(event) => patchForm({ offerings_text: event.target.value })}
                />
              </label>
            </section>
          ) : null}
          {activeSection === 'templates' ? (
            <section className="studio-section">
              <h2>Templates</h2>
              <div className="studio-repeat">
                {TEMPLATE_KEYS.map((key) => (
                  <label key={key}>
                    {key}
                    <textarea
                      rows={3}
                      value={form.templates[key]}
                      onChange={(event) => patchTemplate(key, event.target.value)}
                    />
                  </label>
                ))}
              </div>
            </section>
          ) : null}
          {activeSection === 'marketplace' ? (
            <section className="studio-section">
              <h2>Marketplace</h2>
              <div className="studio-grid">
                <label>
                  Vertical
                  <select
                    value={marketplaceVertical}
                    onChange={(event) =>
                      setMarketplaceVertical(event.target.value as TenantVertical)
                    }
                  >
                    <option value="fitness">Fitness</option>
                    <option value="clinica">Clinica</option>
                    <option value="academia">Academia</option>
                    <option value="estetica">Estetica</option>
                    <option value="servicos">Servicos</option>
                    <option value="pet">Pet</option>
                    <option value="auto">Auto</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>
                <button type="button" className="secondary" onClick={loadMarketplace}>
                  Carregar biblioteca
                </button>
              </div>
              {marketplace ? (
                <div className="metric-columns">
                  <section className="panel metric-table metric-detail-table">
                    <h2>Templates</h2>
                    {marketplace.templates.length ? (
                      <table>
                        <tbody>
                          {marketplace.templates.map((item) => (
                            <tr key={item.item_id}>
                              <th>{item.key}</th>
                              <td>{item.version}</td>
                              <td>
                                <button
                                  type="button"
                                  className="secondary"
                                  disabled={saving === `marketplace-${item.item_id}`}
                                  onClick={() => installMarketplace('template', item.item_id)}
                                >
                                  Instalar draft
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="hint">Sem templates para esta vertical.</p>
                    )}
                  </section>
                  <section className="panel metric-table metric-detail-table">
                    <h2>Flows</h2>
                    {marketplace.flows.length ? (
                      <table>
                        <tbody>
                          {marketplace.flows.map((item) => (
                            <tr key={item.item_id}>
                              <th>{item.name}</th>
                              <td>{item.version}</td>
                              <td>
                                <button
                                  type="button"
                                  className="secondary"
                                  disabled={saving === `marketplace-${item.item_id}`}
                                  onClick={() => installMarketplace('flow', item.item_id)}
                                >
                                  Instalar draft
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="hint">Sem flows para esta vertical.</p>
                    )}
                  </section>
                </div>
              ) : null}
            </section>
          ) : null}
          {activeSection === 'policies' ? (
            <section className="studio-section">
              <h2>Politicas basicas</h2>
              <div className="studio-grid">
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={form.policies.can_share_prices}
                    onChange={(event) =>
                      patchForm({
                        policies: { ...form.policies, can_share_prices: event.target.checked },
                      })
                    }
                  />
                  Bot pode informar precos
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={form.policies.deposit_enabled}
                    onChange={(event) =>
                      patchForm({
                        policies: { ...form.policies, deposit_enabled: event.target.checked },
                      })
                    }
                  />
                  Sinal/deposito habilitado
                </label>
                <label>
                  Politica de preco
                  <select
                    value={form.policies.price_style}
                    onChange={(event) =>
                      patchForm({
                        policies: {
                          ...form.policies,
                          price_style: event.target.value as PriceStyle,
                        },
                      })
                    }
                  >
                    <option value="staff_only">Somente humano</option>
                    <option value="range">Faixa de preco</option>
                    <option value="from">A partir de</option>
                  </select>
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={form.ai.free_reply_enabled}
                    onChange={(event) =>
                      patchForm({ ai: { ...form.ai, free_reply_enabled: event.target.checked } })
                    }
                  />
                  Permitir resposta livre de IA
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={form.ai.human_reply_suggestions_enabled}
                    onChange={(event) =>
                      patchForm({
                        ai: {
                          ...form.ai,
                          human_reply_suggestions_enabled: event.target.checked,
                        },
                      })
                    }
                  />
                  Sugerir resposta para atendente
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={form.ai.case_summary_enabled}
                    onChange={(event) =>
                      patchForm({
                        ai: {
                          ...form.ai,
                          case_summary_enabled: event.target.checked,
                        },
                      })
                    }
                  />
                  Resumo automatico do caso
                </label>
              </div>
            </section>
          ) : null}
          {activeSection === 'required' ? (
            <section className="studio-section">
              <h2>Campos obrigatorios</h2>
              <div className="studio-repeat">
                {INTENTS.map((intent) => (
                  <label key={intent}>
                    {intent} - uma linha por campo: path | pergunta
                    <textarea
                      rows={4}
                      value={form.required_fields_text[intent]}
                      onChange={(event) => patchRequired(intent, event.target.value)}
                    />
                  </label>
                ))}
              </div>
            </section>
          ) : null}
          {activeSection === 'limits' ? (
            <section className="studio-section">
              <h2>Limites anti-loop e IA</h2>
              <div className="studio-grid">
                <label>
                  Timeout esperando info (h)
                  <input
                    type="number"
                    min="1"
                    value={form.anti_loop.waiting_info_timeout_hours}
                    onChange={(event) =>
                      patchForm({
                        anti_loop: {
                          ...form.anti_loop,
                          waiting_info_timeout_hours: Number(event.target.value),
                        },
                      })
                    }
                  />
                </label>
                <label>
                  Max perguntas automaticas
                  <input
                    type="number"
                    min="1"
                    value={form.anti_loop.max_auto_questions_per_case}
                    onChange={(event) =>
                      patchForm({
                        anti_loop: {
                          ...form.anti_loop,
                          max_auto_questions_per_case: Number(event.target.value),
                        },
                      })
                    }
                  />
                </label>
                <label>
                  Max turnos aguardando
                  <input
                    type="number"
                    min="1"
                    value={form.anti_loop.max_waiting_turns}
                    onChange={(event) =>
                      patchForm({
                        anti_loop: {
                          ...form.anti_loop,
                          max_waiting_turns: Number(event.target.value),
                        },
                      })
                    }
                  />
                </label>
                <label>
                  Max prompts nao-texto
                  <input
                    type="number"
                    min="1"
                    value={form.anti_loop.max_non_text_prompts}
                    onChange={(event) =>
                      patchForm({
                        anti_loop: {
                          ...form.anti_loop,
                          max_non_text_prompts: Number(event.target.value),
                        },
                      })
                    }
                  />
                </label>
                <label>
                  Threshold de confianca
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={form.ai.confidence_threshold}
                    onChange={(event) =>
                      patchForm({
                        ai: { ...form.ai, confidence_threshold: Number(event.target.value) },
                      })
                    }
                  />
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={form.ai.auto_ask_missing_info}
                    onChange={(event) =>
                      patchForm({ ai: { ...form.ai, auto_ask_missing_info: event.target.checked } })
                    }
                  />
                  Pedir campo faltante automaticamente
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={form.ai.route_on_low_confidence}
                    onChange={(event) =>
                      patchForm({
                        ai: { ...form.ai, route_on_low_confidence: event.target.checked },
                      })
                    }
                  />
                  Handoff em baixa confianca
                </label>
              </div>
            </section>
          ) : null}
          {activeSection === 'urgency' ? (
            <section className="studio-section">
              <h2>Urgencia e spam</h2>
              <div className="studio-grid">
                <label>
                  Palavras de urgencia
                  <textarea
                    rows={4}
                    value={form.urgency_keywords_text}
                    onChange={(event) => patchForm({ urgency_keywords_text: event.target.value })}
                  />
                </label>
                <label>
                  Palavras de spam
                  <textarea
                    rows={4}
                    value={form.spam_keywords_text}
                    onChange={(event) => patchForm({ spam_keywords_text: event.target.value })}
                  />
                </label>
              </div>
            </section>
          ) : null}
          {activeSection === 'review' ? (
            <section className="studio-section">
              <h2>Revisao</h2>
              <div className="studio-status-grid">
                <span>{hasDraft ? 'Draft salvo' : 'Sem draft salvo'}</span>
                <span>{isDirty ? 'Alteracoes nao salvas' : 'Formulario sincronizado'}</span>
                <span>
                  {validationErrors.length ? 'Config invalida' : 'Config valida no Studio'}
                </span>
                <span>{publishedConfig ? 'Existe publicado' : 'Sem publicado'}</span>
              </div>
              {validationErrors.length ? (
                <div className="studio-errors">
                  {validationErrors.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                </div>
              ) : null}
              <pre className="json-preview">{prettyJson(currentDocument)}</pre>
            </section>
          ) : null}
          <div className="studio-actions">
            <label>
              Motivo da alteracao
              <input
                value={changeReason}
                onChange={(event) => setChangeReason(event.target.value)}
              />
            </label>
            <button
              type="submit"
              disabled={!changeReason.trim() || saving === 'save' || validationErrors.length > 0}
            >
              {saving === 'save' ? 'Salvando...' : 'Salvar draft'}
            </button>
          </div>
        </form>
        <aside className="panel admin-side">
          <form className="admin-form" onSubmit={validateDraft}>
            <h2>Validacao</h2>
            <label>
              Motivo
              <textarea
                value={validationReason}
                onChange={(event) => setValidationReason(event.target.value)}
                rows={3}
              />
            </label>
            <button
              type="submit"
              disabled={
                !validationReason.trim() ||
                saving === 'validate' ||
                validationErrors.length > 0 ||
                isDirty ||
                !hasDraft
              }
            >
              {saving === 'validate' ? 'Validando...' : 'Validar draft'}
            </button>
            <button
              type="button"
              disabled={saving === 'diff' || isDirty || !hasDraft}
              onClick={loadDiff}
            >
              {saving === 'diff' ? 'Comparando...' : 'Comparar diff'}
            </button>
          </form>

          {canPublish ? (
            <>
              <form className="admin-form" onSubmit={publishConfig}>
                <h2>Publicacao</h2>
                <label>
                  Motivo
                  <textarea
                    value={publishReason}
                    onChange={(event) => setPublishReason(event.target.value)}
                    rows={3}
                  />
                </label>
                <button
                  type="submit"
                  disabled={
                    !publishReason.trim() ||
                    saving === 'publish' ||
                    validationErrors.length > 0 ||
                    isDirty ||
                    !hasDraft
                  }
                >
                  {saving === 'publish' ? 'Publicando...' : 'Publicar draft'}
                </button>
              </form>

              <form className="admin-form" onSubmit={rollbackConfig}>
                <h2>Rollback</h2>
                <label>
                  Versao
                  <input
                    value={rollbackVersion}
                    onChange={(event) => setRollbackVersion(event.target.value)}
                  />
                </label>
                <label>
                  Motivo
                  <textarea
                    value={rollbackReason}
                    onChange={(event) => setRollbackReason(event.target.value)}
                    rows={3}
                  />
                </label>
                <button type="submit" disabled={!rollbackReason.trim() || saving === 'rollback'}>
                  {saving === 'rollback' ? 'Restaurando...' : 'Rollback'}
                </button>
              </form>
            </>
          ) : null}

          <section className="audit-list">
            <h2>Versoes</h2>
            {versions.length ? (
              <ul>
                {versions.slice(-8).map((version) => (
                  <li key={version.status + '-' + String(version.config_version ?? 'sem-versao')}>
                    {version.status}: {version.config_version ?? '-'} por{' '}
                    {version.updated_by ?? version.published_by ?? version.created_by ?? '-'} em{' '}
                    {formatDate(version.updated_at ?? version.published_at ?? version.created_at)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="hint">Sem versoes registradas.</p>
            )}
          </section>

          {diffEntries.length ? (
            <section className="audit-list">
              <h2>Diff</h2>
              <ul>
                {diffEntries.slice(0, 20).map((entry) => (
                  <li key={entry.path}>
                    {entry.path}: {diffValue(entry.before)}
                    {' -> '}
                    {diffValue(entry.after)}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="audit-list">
            <h2>Auditoria</h2>
            {auditEvents.length ? (
              <ul>
                {auditEvents.slice(-12).map((event) => (
                  <li key={event.audit_id}>
                    {event.action}: {auditVersionLabel(event)} por {event.actor_id ?? '-'} em{' '}
                    {formatDate(event.created_at)}
                    {event.reason ? ' - ' + event.reason : ''}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="hint">Sem eventos registrados.</p>
            )}
          </section>
        </aside>
      </section>
    </main>
  );
}
